import { defineStore } from "pinia";
import { HoodApi } from "@/services/HoodApi";

interface ProposalStoreState {
  proposals: ProposalListItem[];
  currentProposal: Proposal | null;
  myProposals: ProposalListItem[];
  mySupported: ProposalListItem[];
  loading: boolean;
  error: string | null;
}

export const useProposalStore = defineStore({
  id: "proposals",
  state: (): ProposalStoreState => ({
    proposals: [],
    currentProposal: null,
    myProposals: [],
    mySupported: [],
    loading: false,
    error: null,
  }),
  getters: {
    activeProposals: (state) => {
      return state.proposals.filter(
        (p) =>
          p.status === "gathering_support" ||
          p.status === "ready_for_vote" ||
          p.status === "voting"
      );
    },
    gatheringSupportProposals: (state) => {
      return state.proposals.filter((p) => p.status === "gathering_support");
    },
    readyForVoteProposals: (state) => {
      return state.proposals.filter((p) => p.status === "ready_for_vote");
    },
    votingProposals: (state) => {
      return state.proposals.filter((p) => p.status === "voting");
    },
    getProposalById: (state) => (id: number) => {
      return state.proposals.find((p) => p.id === id);
    },
  },
  actions: {
    async fetchProposals(): Promise<ProposalListItem[]> {
      this.loading = true;
      this.error = null;
      try {
        const proposals = await HoodApi.fetch_proposals();
        this.proposals = proposals;
        return proposals;
      } catch (err) {
        this.error = "Failed to fetch proposals";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchProposal(id: number): Promise<Proposal> {
      this.loading = true;
      this.error = null;
      try {
        const proposal = await HoodApi.fetch_proposal(id);
        this.currentProposal = proposal;
        return proposal;
      } catch (err) {
        this.error = "Failed to fetch proposal";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async createProposal(proposal: NewProposal): Promise<Proposal> {
      this.loading = true;
      this.error = null;
      try {
        const newProposal = await HoodApi.create_proposal(proposal);
        // Add to proposals list
        this.proposals.unshift({
          id: newProposal.id,
          title: newProposal.title,
          description: newProposal.description,
          category: newProposal.category,
          author: newProposal.author,
          status: newProposal.status,
          support_threshold: newProposal.support_threshold,
          support_count: newProposal.support_count,
          has_reached_threshold: newProposal.has_reached_threshold,
          comment_count: 0,
          is_author: true,
          is_supporting: false,
          created_on: newProposal.created_on,
        });
        return newProposal;
      } catch (err) {
        this.error = "Failed to create proposal";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async supportProposal(proposalId: number): Promise<SupportResponse> {
      this.error = null;
      try {
        const response = await HoodApi.support_proposal(proposalId);
        // Update support count in list
        const proposal = this.proposals.find((p) => p.id === proposalId);
        if (proposal) {
          proposal.support_count = response.support_count;
          proposal.has_reached_threshold = response.threshold_reached;
          if (response.threshold_reached) {
            proposal.status = "ready_for_vote";
          }
        }
        // Update current proposal if it matches
        if (this.currentProposal && this.currentProposal.id === proposalId) {
          this.currentProposal.support_count = response.support_count;
          this.currentProposal.has_reached_threshold =
            response.threshold_reached;
          this.currentProposal.supporters.push(response.support);
          if (response.threshold_reached) {
            this.currentProposal.status = "ready_for_vote";
          }
        }
        return response;
      } catch (err) {
        this.error = "Failed to support proposal";
        throw err;
      }
    },

    async withdrawSupport(proposalId: number): Promise<void> {
      this.error = null;
      try {
        const response = await HoodApi.withdraw_support(proposalId);
        // Update support count in list
        const proposal = this.proposals.find((p) => p.id === proposalId);
        if (proposal) {
          proposal.support_count = response.support_count;
        }
        // Refresh current proposal to get updated supporters list
        if (this.currentProposal && this.currentProposal.id === proposalId) {
          await this.fetchProposal(proposalId);
        }
      } catch (err) {
        this.error = "Failed to withdraw support";
        throw err;
      }
    },

    async fetchMyProposals(): Promise<ProposalListItem[]> {
      try {
        const proposals = await HoodApi.fetch_my_proposals();
        this.myProposals = proposals;
        return proposals;
      } catch (err) {
        console.error("Failed to fetch my proposals", err);
        throw err;
      }
    },

    async fetchMySupported(): Promise<ProposalListItem[]> {
      try {
        const proposals = await HoodApi.fetch_my_supported();
        this.mySupported = proposals;
        return proposals;
      } catch (err) {
        console.error("Failed to fetch my supported proposals", err);
        throw err;
      }
    },

    async addComment(
      proposalId: number,
      content: string
    ): Promise<ProposalComment> {
      this.error = null;
      try {
        const comment = await HoodApi.create_proposal_comment({
          proposal: proposalId,
          content,
        });
        // Add comment to current proposal if it matches
        if (this.currentProposal && this.currentProposal.id === proposalId) {
          this.currentProposal.comments.push(comment);
        }
        // Update comment count in list
        const proposal = this.proposals.find((p) => p.id === proposalId);
        if (proposal) {
          proposal.comment_count++;
        }
        return comment;
      } catch (err) {
        this.error = "Failed to add comment";
        throw err;
      }
    },

    clearCurrentProposal(): void {
      this.currentProposal = null;
    },

    clearError(): void {
      this.error = null;
    },
  },
});
